/**
 * Wind Component Analyzer — crosswind and headwind computations for runway analysis.
 * Platform-agnostic: pure math.
 */
import type { RunwayWindAnalysis, WindData } from './types';

export interface RunwayInfo {
  runwayId: string;
  headingDeg: number;
}

export class WindComponentAnalyzer {
  analyzeRunways(
    runways: RunwayInfo[],
    wind: WindData,
    maxCrosswindKts?: number,
  ): RunwayWindAnalysis[] {
    return runways.map(rwy => this.analyzeRunway(rwy, wind, maxCrosswindKts));
  }

  analyzeRunway(
    runway: RunwayInfo,
    wind: WindData,
    maxCrosswindKts?: number,
  ): RunwayWindAnalysis {
    const DEG_TO_RAD = Math.PI / 180;
    const angleDiff = (wind.direction - runway.headingDeg) * DEG_TO_RAD;
    const crosswindKts = Math.abs(wind.speed * Math.sin(angleDiff));
    const headwindKts = wind.speed * Math.cos(angleDiff);
    const limit = maxCrosswindKts ?? Infinity;

    return {
      runwayId: runway.runwayId,
      runwayHeading: runway.headingDeg,
      crosswindKts: Math.round(crosswindKts * 10) / 10,
      headwindKts: Math.round(headwindKts * 10) / 10,
      exceedsCrosswindLimit: crosswindKts > limit,
    };
  }

  bestRunway(runways: RunwayInfo[], wind: WindData): RunwayWindAnalysis | null {
    if (runways.length === 0) return null;
    const analyses = this.analyzeRunways(runways, wind);
    return analyses.reduce((best, curr) =>
      curr.crosswindKts < best.crosswindKts ? curr : best,
    );
  }
}
